import * as React from 'react'
import AppContext, { appStateReducer, defaultAppState } from './AppContext'
import { useEffect, useReducer } from 'react'
import AppRouter from './AppRouter'
import { scheduleDailyListNotification } from '../utils/helpers/notifications'
import { Capacitor } from '@capacitor/core'
import { SocialLogin } from '@capgo/capacitor-social-login'
import ThemeProvider from '@mui/material/styles/ThemeProvider'
import { appTheme } from '../ui/theme/theme'

// The approved app typography must not inherit saved font experiments.
const TypographyPreview =
  import.meta.env.DEV &&
  (new URLSearchParams(window.location.search).get('typography') === '1' ||
    new URLSearchParams(window.location.search).has('rt-font'))
    ? React.lazy(() => import('../dev/typography/TypographyPreview'))
    : null

function App(): JSX.Element {
  const [appState, appReducer] = useReducer(appStateReducer, defaultAppState)

  useEffect(() => {
    scheduleDailyListNotification().catch(() => {
      // no-op: notification scheduling is best-effort
    })
  }, [])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const googleConfig =
      import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID || import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID
        ? {
            iOSClientId: import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID,
            iOSServerClientId: import.meta.env.VITE_GOOGLE_IOS_SERVER_CLIENT_ID,
            webClientId: import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID,
            mode: 'online' as const,
          }
        : undefined

    const appleConfig = import.meta.env.VITE_APPLE_CLIENT_ID
      ? {
          clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
          redirectUrl: '',
        }
      : undefined

    if (!googleConfig && !appleConfig) return

    SocialLogin.initialize({
      google: googleConfig,
      apple: appleConfig,
    }).catch(() => {
      // no-op: initialization is best-effort
    })
  }, [])

  return (
    <>
      <div className="safe-area">
        <ThemeProvider theme={appTheme}>
          <AppContext.Provider value={{ appState, appReducer }}>
            <AppRouter></AppRouter>
            {TypographyPreview && (
              <React.Suspense fallback={null}>
                <TypographyPreview />
              </React.Suspense>
            )}
          </AppContext.Provider>
        </ThemeProvider>
      </div>
    </>
  )
}

export default App
